import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, assetUrl } from "@/lib/api";
import { Diamond, CircleNotch, ShoppingCart } from "@phosphor-icons/react";
import { toast } from "sonner";
import ThreeViewer from "@/pages/ThreeViewer";
import Assembly3D from "@/pages/Assembly3D";
import { useTranslation } from "react-i18next";

const PRESET_ZONES = ["center", "topleft", "topright", "left", "right", "top", "bottom"];
// Virtual parts so a photo-only product reuses the 3D customizer options (stones, enamel, logo zones).
const simpleParts = (p) => [
  ...Array.from({ length: Number(p.stone_sets || 0) }, (_, i) => ({ id: `vs${i + 1}`, role: "stones", stoneSet: String(i + 1), slot: "decor", name: `Set ${i + 1}` })),
  ...(p.simple_enamel ? [{ id: "ve1", role: "enamel", slot: "decor", name: "", color: (p.enamel_palette || [])[0]?.hex || "#9B1B1B" }] : []),
  ...(p.logo_areas || []).map((a) => ({ id: `vl_${a.id}`, role: "logo", slot: a.id, slot_anchor: true, name: a.label,
    zone_label: PRESET_ZONES.includes(a.id) ? undefined : a.label,
    allowed_logo_ids: a.allowed_logo_ids || [], customer_upload_enabled: !!a.customer_upload_enabled })),
];

export default function Customizer() {
  const { t } = useTranslation();
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [err, setErr] = useState(null);
  const [promo, setPromo] = useState({ code: null, pct: 0 });
  const storeSlug = new URLSearchParams(window.location.search).get("store");
  const showcase = new URLSearchParams(window.location.search).get("showcase") === "1";

  useEffect(() => {
    // Capture salesperson info from a rep's personalized link (?rep=Nombre&rep_tel=+1...)
    if (storeSlug) {
      const q = new URLSearchParams(window.location.search);
      const rep = q.get("rep");
      const repTel = q.get("rep_tel") || q.get("rep_phone");
      if (rep || repTel) {
        try { localStorage.setItem(`hephy_rep_${storeSlug}`, JSON.stringify({ name: rep || "", phone: repTel || "" })); } catch { /* sandboxed */ }
      }
    }
  }, [storeSlug]);

  useEffect(() => {
    // Seller promo link discount (?promo=CODE on the URL, or saved by the storefront).
    if (!storeSlug) return;
    const q = new URLSearchParams(window.location.search);
    let code = q.get("promo");
    if (!code) { try { code = localStorage.getItem(`hephy_promo_${storeSlug}`); } catch { /* sandboxed */ } }
    if (!code) return;
    code = code.toUpperCase();
    api.get(`/public/promo/${code}`, { withCredentials: false })
      .then((r) => { if (r.data?.valid && r.data.percent > 0) setPromo({ code, pct: r.data.percent }); })
      .catch(() => {});
  }, [storeSlug]);

  useEffect(() => {
    // Promo forwarded by the Wix site (masterPage.js reads ?promo= and posts HEPHY_PROMO).
    const onMsg = (e) => {
      let d = e.data;
      if (typeof d === "string") { try { d = JSON.parse(d); } catch { return; } }
      if (!d || d.type !== "HEPHY_PROMO" || !d.promo) return;
      const code = String(d.promo).toUpperCase();
      api.get(`/public/promo/${code}`, { withCredentials: false })
        .then((r) => { if (r.data?.valid && r.data.percent > 0) setPromo({ code, pct: r.data.percent }); })
        .catch(() => {});
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    // withCredentials:false → works from the opaque-origin Wix sandbox iframe (CORS `*`).
    api.get(`/public/products/${productId}`, { withCredentials: false })
      .then((r) => setProduct(r.data))
      .catch((e) => setErr(apiError(e)));
  }, [productId]);

  if (err) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-neutral-500">{err}</div>;
  if (!product) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><CircleNotch size={30} className="animate-spin text-gold" /></div>;

  if (product.model_parts?.length) return <Assembly3D product={product} storeMode={!!storeSlug} storeSlug={storeSlug} promoPct={promo.pct || product.sale_percent || 0} promoCode={promo.code} showcase={showcase} />;
  if (product.catalog_type === "simple" && product.simple_custom) return <Assembly3D product={{ ...product, model_parts: simpleParts(product) }} photoMode storeMode={!!storeSlug} storeSlug={storeSlug} promoPct={promo.pct || product.sale_percent || 0} promoCode={promo.code} />;
  if (product.model_3d_url) return <ThreeViewer product={product} />;
  if ((product.catalog_type && product.catalog_type !== "custom3d")) return <SimpleProductView product={product} slug={storeSlug || product.store?.slug} />;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center text-center px-6 gap-2" data-testid="no-3d-message">
      <Diamond size={30} weight="fill" className="text-gold" />
      <h2 className="font-serif text-2xl">{product.name}</h2>
      <p className="text-neutral-500 max-w-md">{t("customizerpage.noModel")}</p>
    </div>
  );
}

function SimpleProductView({ product, slug }) {
  const { t } = useTranslation();
  const variants = product.variants || [];
  const [variant, setVariant] = useState(variants[0]?.label || "");
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(false);
  const img = product.gallery_urls?.[0] || product.base_url;
  const priceOf = () => { const v = variants.find((x) => String(x.label) === String(variant)); return Number((v && v.price) || product.price || 0); };
  const add = async () => {
    if (!slug) { toast.error("Esta tienda no está disponible para compra directa."); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem(`hephy_cart_${slug}`) || undefined;
      const { data } = await api.post("/public/cart/add", { token, slug, product_id: product.id, metal_id: "", variant_label: variant, quantity: 1 }, { withCredentials: false });
      localStorage.setItem(`hephy_cart_${slug}`, data.token);
      setAdded(true);
      toast.success("Añadido al carrito");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-neutral-900 flex items-center justify-center px-6 py-10" data-testid="simple-product-view">
      <div className="max-w-3xl w-full grid md:grid-cols-2 gap-8 items-center">
        <div className="aspect-square rounded-2xl overflow-hidden bg-white border border-black/10 flex items-center justify-center">
          {img ? <img src={assetUrl(img)} alt={product.name} className="w-full h-full object-cover" data-testid="simple-product-image" /> : <Diamond size={48} className="text-neutral-300" />}
        </div>
        <div>
          <h1 className="font-serif text-3xl" data-testid="simple-product-name">{product.name}</h1>
          {product.description && <p className="text-sm text-neutral-600 mt-2 whitespace-pre-line">{product.description}</p>}
          <div className="text-2xl font-bold mt-4" data-testid="simple-product-price">${priceOf().toLocaleString()}</div>
          {variants.length > 0 && (
            <div className="mt-4">
              <label className="text-xs text-neutral-500 block mb-1">{product.variant_name || "Opción"}</label>
              <select value={variant} onChange={(e) => setVariant(e.target.value)} data-testid="simple-variant-select" className="w-full h-11 rounded-lg border border-black/15 px-3 text-sm bg-white">
                {variants.map((v) => <option key={v.label} value={v.label}>{v.label}{v.price ? ` — $${Number(v.price).toLocaleString()}` : ""}</option>)}
              </select>
            </div>
          )}
          {product.catalog_type === "digital"
            ? <p className="mt-4 text-sm text-neutral-500">Producto digital. Recibirás el acceso por correo tras la compra.</p>
            : null}
          <button onClick={add} disabled={busy} data-testid="simple-add-cart-btn" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-semibold bg-gold text-obsidian hover:bg-gold/90 disabled:opacity-60 w-full">
            <ShoppingCart size={16} weight="fill" /> {busy ? "…" : (added ? "Añadido ✓" : "Añadir al carrito")}
          </button>
          {slug && <a href={`/tienda/${slug}/carrito`} className="mt-3 block text-center text-sm text-gold hover:underline" data-testid="simple-go-cart">Ir al carrito →</a>}
        </div>
      </div>
    </div>
  );
}
